<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import moment from 'moment';

const props = defineProps({
    options: {
        type: Object,
        required: true
    },
});

defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const logs = ref([]);
const loading = ref(true);
const orderId = props.options.orderId;

// --- Methods ---

const formatDate = (timestamp) => {
    // Assuming timestamp from server is in seconds (based on original * 1000)
    return moment(timestamp * 1000).format('DD MMM YYYY, HH:mm');
};

const fetchLogs = async () => {
    loading.value = true;
    try {
        // Replacing asyncComputed with a standard store dispatch
        logs.value = await store.dispatch('orderLogs/get', orderId);
    } catch (error) {
        console.error("Failed to fetch order logs:", error);
    } finally {
        loading.value = false;
    }
};

// --- Lifecycle ---

onMounted(() => {
    fetchLogs();
});
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Order Logs</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="mt-2 text-muted">Loading logs...</p>
                </div>

                <div v-else-if="logs.length === 0" class="text-center p-5">
                    <p class="text-muted">No logs found for this order.</p>
                </div>

                <div v-else class="list-group">
                    <div
                        v-for="(log, index) in logs"
                        :key="log.id || index"
                        class="list-group-item pb-0"
                    >
                        <div
                            :data-bs-target="'#log-' + index"
                            data-bs-toggle="collapse"
                            class="py-2"
                            style="cursor: pointer;"
                        >
                            <span class="float-end text-muted small">
                                {{ formatDate(log.createdDate) }}
                            </span>

                            <p class="mb-0">
                                <i class="bi bi-journal-text me-2"></i>
                                <strong>{{ log.title }}</strong>
                            </p>
                        </div>

                        <div :id="'log-' + index" class="collapse mt-2">
                            <div class="bg-light p-3 border rounded mb-3">
                                <pre class="log-pre mb-0"><code>{{ log.data }}</code></pre>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.log-pre {
    white-space: pre-wrap;       /* css-3 */
    white-space: -moz-pre-wrap;  /* Mozilla, since 1999 */
    white-space: -pre-wrap;      /* Opera 4-6 */
    white-space: -o-pre-wrap;    /* Opera 7 */
    word-wrap: break-word;       /* Internet Explorer 5.5+ */
    font-size: 0.85rem;
    color: #212529;
}

.list-group-item:hover {
    background-color: rgba(0,0,0,0.02);
}
</style>